import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';

const MAX_IDS = 1000;

export function useBulkSelection() {
  const [selectMode, setSelectMode] = useState(null);
  const [pickedIds, setPickedIds] = useState(() => new Set());
  const [excludedIds, setExcludedIds] = useState(() => new Set());

  const clearSelection = useCallback(() => {
    setSelectMode(null);
    setPickedIds(new Set());
    setExcludedIds(new Set());
  }, []);

  const isSelected = (id) =>
    selectMode === 'all_matching' ? !excludedIds.has(id) : pickedIds.has(id);

  const toggleSelection = (id) => {
    if (selectMode === 'all_matching') {
      const next = new Set(excludedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      if (next.size > MAX_IDS) {
        toast.error('At most 1000 excluded leads.');
        return;
      }
      setExcludedIds(next);
      return;
    }
    const next = new Set(pickedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    if (next.size > MAX_IDS) {
      toast.error('At most 1000 selected leads.');
      return;
    }
    setPickedIds(next);
    setSelectMode(next.size ? 'ids' : null);
  };

  const headerChecked = (currentPageItems = []) =>
    selectMode === 'all_matching' &&
    currentPageItems.length > 0 &&
    currentPageItems.every((item) => !excludedIds.has(item.id));

  const toggleSelectAll = ({ currentPageItems = [] }) => {
    if (headerChecked(currentPageItems)) {
      clearSelection();
      return;
    }
    setSelectMode('all_matching');
    setPickedIds(new Set());
    setExcludedIds(new Set());
  };

  const selectedCountFor = (totalCount = 0) =>
    selectMode === 'all_matching'
      ? Math.max(0, totalCount - excludedIds.size)
      : pickedIds.size;

  return {
    selectedIds: pickedIds,
    selectMode,
    excludedIds,
    toggleSelection,
    clearSelection,
    isSelected,
    headerChecked,
    toggleSelectAll,
    isFetchingAllIds: false,
    selectedCountFor,
  };
}

export default useBulkSelection;
